// ══════════════════════════════════════════════════════════════════════════════
//  DigitalProductDetailScreen.jsx
//  المسار: src/screens/client/DigitalProductDetailScreen.jsx
// ══════════════════════════════════════════════════════════════════════════════

import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import supabaseService from '../services/supabaseService';
import { toast } from 'react-hot-toast';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FiArrowLeft, FiStar, FiBook, FiArchive, FiLink,
  FiBookOpen, FiCheckCircle, FiShoppingCart, FiShoppingBag,
  FiX, FiZoomIn
} from 'react-icons/fi';
import MediaCarousel, { buildMediaList } from './MediaCarousel';

// ── helpers ───────────────────────────────────────────────
function typeIcon(t) {
  if (t === 'pdf')  return <FiBook    className="w-4 h-4" />;
  if (t === 'zip')  return <FiArchive className="w-4 h-4" />;
  if (t === 'link') return <FiLink    className="w-4 h-4" />;
  return <FiBookOpen className="w-4 h-4" />;
}
function typeLabel(t) {
  if (t === 'pdf')  return 'كتاب PDF';
  if (t === 'zip')  return 'ملف مضغوط ZIP';
  if (t === 'link') return 'رابط رقمي';
  return 'منتج رقمي';
}
function typeBadgeLabel(t) {
  if (t === 'pdf')  return 'كتاب PDF';
  if (t === 'zip')  return 'ملف ZIP';
  if (t === 'link') return 'رابط';
  return 'رقمي';
}

// ── معرض الصور ──────────────────────────────────────────
function ImageGallery({ images, title, onClose }) {
  const [current, setCurrent] = useState(0);
  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black z-50 flex flex-col" dir="rtl"
    >
      {/* header */}
      <div className="flex items-center gap-3 p-4 pt-safe">
        <button onClick={onClose}
          className="w-9 h-9 flex items-center justify-center rounded-xl bg-white/10 text-white hover:bg-white/20 transition flex-shrink-0">
          <FiX className="w-5 h-5" />
        </button>
        <p className="flex-1 text-sm font-bold text-white truncate">{title}</p>
        <span className="text-xs text-white bg-white/15 px-3 py-1 rounded-full font-semibold">
          {current + 1} / {images.length}
        </span>
      </div>

      {/* main image */}
      <div className="flex-1 flex items-center justify-center overflow-hidden">
        <AnimatePresence mode="wait">
          <motion.img
            key={current}
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            src={images[current]} alt={`${current + 1}`}
            className="max-w-full max-h-full object-contain"
          />
        </AnimatePresence>
      </div>

      {/* arrows */}
      {images.length > 1 && (
        <>
          {current > 0 && (
            <button onClick={() => setCurrent(c => c - 1)}
              className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center rounded-xl bg-white/15 text-white hover:bg-white/25 transition">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          )}
          {current < images.length - 1 && (
            <button onClick={() => setCurrent(c => c + 1)}
              className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center rounded-xl bg-white/15 text-white hover:bg-white/25 transition">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
          )}
        </>
      )}

      {/* dots */}
      {images.length > 1 && (
        <div className="flex justify-center gap-1.5 pb-8 pt-3">
          {images.map((_, i) => (
            <button key={i} onClick={() => setCurrent(i)}
              className={`rounded-full transition-all ${i === current ? 'w-5 h-1.5 bg-white' : 'w-1.5 h-1.5 bg-white/40'}`}
            />
          ))}
        </div>
      )}
    </motion.div>
  );
}

// ── صف معلومة ─────────────────────────────────────────────
function InfoRow({ icon, label, value, valueColor }) {
  return (
    <div className="flex items-center gap-3 bg-gray-50 rounded-xl px-3.5 py-3 border border-gray-100/80">
      <span className="text-gray-300">{icon}</span>
      <span className="text-xs text-gray-400 flex-1">{label}</span>
      <span className={`text-xs font-bold ${valueColor || 'text-gray-900'}`}>{value}</span>
    </div>
  );
}

// ══════════════════════════════════════════════════════════
//  الشاشة الرئيسية
// ══════════════════════════════════════════════════════════
export default function DigitalProductDetailScreen() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();

  // البيانات تُمرَّر عبر navigate state
  const { service, isOwned: initOwned = false, isInCart: initInCart = false } = location.state || {};

  const [inCart,   setInCart]   = useState(initInCart);
  const [isOwned]               = useState(initOwned);
  const [buying,   setBuying]   = useState(false);
  const [showGallery, setShowGallery] = useState(false);

  if (!service) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50" dir="rtl">
        <div className="text-center">
          <p className="text-gray-400 mb-4">لم يتم العثور على المنتج</p>
          <button onClick={() => navigate(-1)} className="bg-gray-900 text-white px-4 py-2 rounded-xl text-sm font-bold">
            رجوع
          </button>
        </div>
      </div>
    );
  }

  const s = service;
  const hasCover = s.imageUrl?.length > 0;

  // ── شراء مباشر (بدون سلة) ─────────────────────────────
  const handleBuyNow = async () => {
    if (!user?.id) { toast.error('يجب تسجيل الدخول أولاً'); return; }
    setBuying(true);
    try {
      const alreadyOwned = await supabaseService.hasClientPurchased({ clientId: user.id, serviceId: s.id });
      if (alreadyOwned) { toast.error('اشتريت هذا المنتج مسبقاً'); setBuying(false); return; }

      const { data: detail } = await supabaseService.client
        .from('services').select('provider_id').eq('id', s.id).single();

      await supabaseService.purchaseDigitalProduct({
        clientId:   user.id,
        serviceId:  s.id,
        providerId: detail.provider_id,
        amount:     s.price || 0,
      });
      toast.success('✓ تم الشراء بنجاح! يمكنك الآن الوصول للمنتج من مكتبتك');
      navigate(-1);
    } catch (e) {
      toast.error(e.message || 'فشل الشراء');
    } finally {
      setBuying(false);
    }
  };

  // ── إضافة/إزالة من السلة ──────────────────────────────
  const handleAddToCart = () => {
    if (isOwned) return;
    setInCart(v => !v);
    // تحديث السلة في الصفحة السابقة يتم عند العودة
    navigate(-1, { state: { toggleCartFor: s.id } });
  };

  // ══════════════════════════════════════════════════════
  return (
    <div className="min-h-screen bg-white flex flex-col" dir="rtl">

      {/* ── hero + appbar ─────────────────────────────────── */}
      <div className="relative w-full" style={{ height: 300 }}>
        {/* صورة الغلاف + معرض الوسائط */}
        <MediaCarousel
          media={buildMediaList(s)}
          title={s.name}
          height="h-[300px]"
          objectFit="object-cover"
          showDots={true}
        />

        {/* تدرج سفلي — pointer-events-none حتى لا يعترض اللمس */}
        <div className="absolute inset-0 bg-gradient-to-t from-white via-transparent to-transparent pointer-events-none" />

        {/* زر رجوع */}
        <div className="absolute top-0 right-0 left-0 flex items-center gap-3 p-4 pt-safe z-10">
          <button onClick={() => navigate(-1)}
            className="w-9 h-9 flex items-center justify-center rounded-xl bg-black/35 text-white hover:bg-black/50 transition flex-shrink-0">
            <FiArrowLeft className="w-3.5 h-3.5" />
          </button>
        </div>

        {/* badge نوع */}
        <div className="absolute bottom-4 left-4 flex items-center gap-1.5 bg-black/50 text-white text-xs font-semibold px-2.5 py-1.5 rounded-lg border border-white/10 z-10">
          {typeIcon(s.productType)}
          <span>{typeBadgeLabel(s.productType)}</span>
        </div>
      </div>

      {/* ── محتوى ────────────────────────────────────────── */}
      <div className="flex-1 overflow-y-auto pb-28">
        <div className="px-5 pt-1 space-y-5">

          {/* الاسم */}
          <h1 className="text-xl font-black text-gray-900 leading-tight tracking-tight">{s.name || 'منتج رقمي'}</h1>

          {/* السعر + التقييم */}
          <div className="flex items-center justify-between">
            <span className="text-3xl font-black text-gray-900 tracking-tight">
              {s.price > 0 ? `${Math.round(s.price)} د.ج` : 'مجاني'}
            </span>
            {s.averageRating > 0 && (
              <div className="flex items-center gap-1.5 bg-gray-100 border border-gray-200 px-3 py-1.5 rounded-xl">
                <FiStar className="w-3.5 h-3.5 text-yellow-400 fill-current" />
                <span className="text-sm font-bold text-gray-900">{s.averageRating.toFixed(1)}</span>
              </div>
            )}
          </div>

          <hr className="border-gray-100" />

          {/* الوصف */}
          {s.description && (
            <div>
              <p className="text-sm font-bold text-gray-900 mb-2">عن المنتج</p>
              <p className="text-sm text-gray-400 leading-relaxed">{s.description}</p>
            </div>
          )}

          {/* صور المعاينة */}
          {s.previewImages?.length > 0 && (
            <div>
              <p className="text-sm font-bold text-gray-900 mb-3">صور المعاينة</p>
              <div className="flex gap-2.5 overflow-x-auto pb-1 scrollbar-hide">
                {s.previewImages.map((img, i) => (
                  <button key={i} onClick={() => setShowGallery(true)}
                    className="flex-shrink-0 w-32 h-32 rounded-xl overflow-hidden relative bg-gray-100 group">
                    <img src={img} alt={`preview-${i}`} className="w-full h-full object-cover" onError={e => { e.target.style.display='none'; }} />
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/15 transition flex items-center justify-center">
                      <FiZoomIn className="w-5 h-5 text-white opacity-0 group-hover:opacity-100 transition" />
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* معلومات المنتج */}
          <div className="space-y-2">
            <InfoRow icon={typeIcon(s.productType)} label="نوع الملف" value={typeLabel(s.productType)} />
            {isOwned && (
              <InfoRow
                icon={<FiCheckCircle className="w-4 h-4 text-green-500" />}
                label="الحالة"
                value="مشترى — موجود في مكتبتك"
                valueColor="text-green-600"
              />
            )}
          </div>
        </div>
      </div>

      {/* ── زر الشراء ────────────────────────────────────── */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-100 px-5 py-3 pb-safe z-30">
        {isOwned ? (
          <div className="h-13 flex items-center justify-center gap-2 rounded-xl border border-green-200 bg-green-50">
            <FiCheckCircle className="w-4 h-4 text-green-500" />
            <span className="text-sm font-extrabold text-green-600">مشترى بالفعل</span>
          </div>
        ) : s.price > 0 ? (
          // منتج بسعر — خيارين: سلة أو شراء مباشر
          <div className="flex gap-2.5">
            <button
              onClick={() => { setInCart(v => !v); navigate(-1); }}
              className={`flex-shrink-0 h-13 px-4 py-3 rounded-xl border font-extrabold text-sm flex items-center gap-2 transition-all
                ${inCart ? 'bg-gray-100 border-gray-300 text-gray-600' : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'}`}
            >
              <FiShoppingCart className="w-4 h-4" />
              {inCart ? 'في السلة' : 'أضف'}
            </button>
            <button
              onClick={handleBuyNow} disabled={buying}
              className={`flex-1 h-13 py-3 rounded-xl font-extrabold text-sm flex items-center justify-center gap-2 transition-all
                ${buying ? 'bg-gray-300 text-gray-500 cursor-not-allowed' : 'bg-gray-900 text-white hover:bg-gray-800 active:scale-[0.98]'}`}
              style={{ boxShadow: buying ? 'none' : '0 4px 14px rgba(0,0,0,.18)' }}
            >
              {buying
                ? <div className="w-5 h-5 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                : <>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                    </svg>
                    شراء الآن — {Math.round(s.price)} د.ج
                  </>
              }
            </button>
          </div>
        ) : (
          // مجاني
          <button
            onClick={handleBuyNow} disabled={buying}
            className={`w-full h-13 py-3 rounded-xl font-extrabold text-sm flex items-center justify-center gap-2 transition-all
              ${buying ? 'bg-gray-300 text-gray-500 cursor-not-allowed' : 'bg-gray-900 text-white hover:bg-gray-800 active:scale-[0.98]'}`}
            style={{ boxShadow: buying ? 'none' : '0 4px 14px rgba(0,0,0,.18)' }}
          >
            {buying
              ? <div className="w-5 h-5 border-2 border-white/40 border-t-white rounded-full animate-spin" />
              : <><FiShoppingBag className="w-4 h-4" /> احصل عليه مجاناً</>
            }
          </button>
        )}
      </div>

      {/* ── معرض الصور ──────────────────────────────────── */}
      <AnimatePresence>
        {showGallery && s.previewImages?.length > 0 && (
          <ImageGallery images={s.previewImages} title={s.name} onClose={() => setShowGallery(false)} />
        )}
      </AnimatePresence>
    </div>
  );
}